import Vue from "vue";
import moment from "moment";
import MediaCluster from "@/domain/photoSearch/mediaCluster";
import TimeLine from "@/domain/photoSearch/timeLine";
import GallerySorting from "@/domain/core/constant/gallerySorting";
import Downloader from "@/domain/core/utils/downloader";
import {cloneDeep, debounce} from "lodash";
import {MultipleFacesError} from "@/domain/core/exception/exceptions";
import photoArchiveService from "@/domain/photoArchive/service";

const maxMediaInFilter = 50 // this is duplicated in core PhotoSearchViewSet.PAGINATED_FILTER_NUM, the numbers MUST coincide

const state = {
    media: {
        received: {},
        storage: {},
        clusters: {},
        clusterBy: MediaCluster.BY_DAY,
        first: null,
        last: null,
        hasBefore: true,
        hasAfter: true,
        sorting: GallerySorting.shot_at_asc,
        defaultSorting: GallerySorting.shot_at_asc,
        hasTimeline: true,
        hideTimeline: false,
        lastCreatedAt: null,
        lastProcessedAt: null,
    },
    timeline: {
        storage: {},
        clusters: [],
        sorted: [],
    },
    filter: {
        options: {},
        value: {
            date_range: {}
        },
    },
    search: {
        query: {
            file_name: null,
            id: null
        },
        storage: null,
        results: null,
        result: null,
    },
    faceSearch: {
        searching: false,
        error: null,
        uploadedImage: null,        // URL.createObjectURL for preview
        uploadedFile: null,          // Original File/Blob
        detectedFaces: [],           // Array of face_bbox if multiple
        results: [],                 // Cluster results from API
        selectedCluster: null,       // For badge display
    },
    selected: {
        filters: [],
        ids: {},
        deselected: {},
        shiftPressed: false,
        lastSelected: null,
        lastOver: null,
        hovering: {},
        serverCount: null,
    },
    availableTags: null,
    tagDefinitions: [],
    clusterNamePattern: '',
    galleries: null,
    pica_service_id: null,
    gallery_id: null,
    timeZone: null,
    permissions: {
        canUpdateTags: false,
        canFilterByPhotographers: false,
        canFilterByTags: false,
        canFilterByPicaCode: false,
        canFilterByRaceNumber: false,
        canFilterByShotAt: false,
        canFilterByDeliveryStatus: false,
        canDownload: false,
        canDownloadSingle: false,
        canSetGallery: false,
        canCopy: false,
        canDelete: false,
        canSortMedia: false,
        canUpload: false,
        canSearchByFileName: false,
        canSearchById: false,
        canSoftDelete: false,
        canRestore: false,
        canSeeId: false,
        canEditMediaUploadedByOther: false,
        canExportMediaPicaCode: false,
        canSeeDelivered: false,
        canSeeDeliverable: false,
        canSeeCheckin: false,
        canSeeFaces: false,
        canUpdateFaceTags: false,
        canSeePhotographer: false,
        canDeleteFace: false,
        canGenerateCheckinFromFace: false,
        canSetPicaCode: false,
        canUnlinkPicaCode: false,
        canArchive: false,
    },
    configLoading: true,
    tabLoading: true,
    detail: null,
}

function updateMediaState(state) {
    const clusterManager = new MediaCluster(state.media.clusterBy, state.media.sorting)
    state.media.sorted = clusterManager.getSortedMedia(state.media.storage)
    state.media.clusters = clusterManager.getClusters(state.media.sorted, state.timeline.clusters)
    state.media.first = state.media.sorted[0]
    state.media.last = state.media.sorted[state.media.sorted.length - 1]
}

function getCursor(getters, media) {
    if ([GallerySorting.shot_at_asc, GallerySorting.shot_at_desc].includes(getters.sorting))
        return {date: media.shot_at}
    if ([GallerySorting.upload_asc, GallerySorting.upload_desc].includes(getters.sorting))
        return {cursor: media.id}  // Use cursor for ID-based sorting, not date
    if (getters.sorting === GallerySorting.custom)
        return {cursor: media.order}
    throw new Error('Invalid sorting')
}

function createActionFilters(filters, ids = null) {
    if (ids === null || ids.length === 0) return filters
    return [{ids: ids}]
}

export const getters = {
    configLoading: state => state.configLoading,
    tabLoading: state => state.tabLoading,
    hasTimeline: state => state.media.hasTimeline,
    hideTimeline: state => state.media.hideTimeline,
    lastCreatedAt: state => state.media.lastCreatedAt,
    lastProcessedAt: state => state.media.lastProcessedAt,
    hasMedia: state => Object.keys(state.media.storage).length > 0,
    numMedia: state => Object.keys(state.media.storage).length,
    hasActiveFilters: (state, getters) => getters.activeFilters.length > 0,
    singleMedia: state => id => state.media.storage[id],
    sorting: state => {
        if (state.media.sorting) return state.media.sorting
        return localStorage.getItem('photoSearch-sort-' + state.pica_service_id + '-' + state.gallery_id) ?? null
    },
    defaultSorting: state => state.media.defaultSorting,
    clusters: state => state.media.clusters,
    media: state => state.media.sorted,
    timeline: state => state.timeline.clusters,
    clusterBy: state => state.media.clusterBy,
    timeZone: state => state.timeZone,
    hasBefore: state => state.media.hasBefore,
    hasAfter: state => state.media.hasAfter,
    firstMedia: state => state.media.first,
    lastMedia: state => state.media.last,
    pica_service_id: state => state.pica_service_id,
    gallery_id: state => state.gallery_id,
    route_param: state => {
        return {
            pica_service_id: state.pica_service_id,
            gallery_id: state.gallery_id
        }
    },
    filters: state => state.filter.value,
    selectedFilters: state => {
        let filters = cloneDeep(state.selected.filters)
        let ids = Object.keys(state.selected.ids)
        if (ids.length > 0)
            filters.push({ids: ids})
        let deselected = Object.keys(state.selected.deselected)
        if (deselected.length > 0)
            filters.push({exclude_ids: deselected})
        return filters
    },
    options: state => state.filter.options,
    permissions: state => state.permissions,
    searchResults: state => state.search.results,
    searchResult: state => state.search.result,
    searchQuery: state => state.search.query,
    availableTags: state => state.availableTags,
    tagDefinitions: state => state.tagDefinitions,
    clusterNamePattern: state => state.clusterNamePattern,
    galleries: state => state.galleries,
    targetGalleries: state => {
        if (!state.galleries) return []
        return state.galleries.map(gallery => {
            const sub = gallery.sub.filter(sub => sub.id !== state.gallery_id && !sub.passive)

            if (gallery.passive || gallery.id === state.gallery_id) {
                if (sub.length === 0) return null
                return {
                    ...gallery,
                    sub: sub
                }
            }

            sub.unshift({
                id: gallery.id,
                name: gallery.name,
                is_public: gallery.is_public,
            })
            return {
                ...gallery,
                sub: sub
            }
        }).filter(gallery => gallery !== null)
    },
    mediaTags: state => tags => {
        if (!tags || !Array.isArray(tags)) return []
        if (state.gallery_id !== null)
            tags = tags.filter(tag => !tag.startsWith('g:'))
        return tags.map(tag => {
            if (tag.startsWith('g:')) {
                if (state.galleries === null) return tag
                const idGallery = parseInt(tag.substring(2))
                const gallery = state.galleries.find(gallery => gallery.id === idGallery)
                if (gallery) return gallery.name
                return tag
            }
            return tag
        })
    },
    activeFilters: state => {
        const filters = Object.keys(state.filter.value).map(key => {
            const value = state.filter.value[key]
            if (key === 'date_range') {
                if (value.start && value.end)
                    return {
                        type: key,
                        value: `${moment(value.start).format('YYYY-MM-DD')} - ${moment(value.end).format('YYYY-MM-DD')}`
                    }
                if (value.start)
                    return {type: key, value: `From ${moment(value.start).format('YYYY-MM-DD')}`}
                if (value.end)
                    return {type: key, value: `To ${moment(value.end).format('YYYY-MM-DD')}`}
                return null
            } else if (value)
                return {type: key, value: value}
            else
                return null
        }).filter(filter => filter !== null)
        return filters
    },
    stats: (state, getters, rootState, rootGetters) => {
        // gallery stats non si aggiorna anche se carico le foto, le riceve etc
        // inoltre c'è da considerare che questi dati sono in differetiva, mentre la funzione remainingUpload ha dati più aggiornati
        if (state.gallery_id > 0)
            return rootGetters['uploader/galleryStats']
        return rootGetters['uploader/eventStats']
    },
    detail: state => state.detail,
    isMediaSelected: state => id => {
        if (state.selected.deselected[id]) return false
        if (state.media.storage[id].selected) return true
        return state.selected.ids[id] ?? false
    },
    isMediaOvering: state => id => {
        return state.selected.hovering[id] ?? false
    },
    numSelected: state => {
        // Se abbiamo un count dal server, usare solo quello
        // (il backend già considera filters + ids + exclude_ids)
        if (state.selected.serverCount !== null) {
            return state.selected.serverCount
        }

        // Altrimenti conta localmente
        let num = Object.keys(state.selected.ids).length
        num -= Object.keys(state.selected.deselected).length
        return num
    },
    selectedMedia: state => {
        return Object.values(state.media.storage).filter(photo => {
            const id = photo.id
            if (state.selected.deselected[id]) return false
            if (photo.selected) return true
            return state.selected.ids[id] ?? false
        })
    },
    isShiftPressed: state => state.selected.shiftPressed,
    idsInProcessing: state => {
        return state.media.sorted.filter(media => !media.is_processed).map(media => media.id)
    },
    // Face Search getters
    isFaceSearching: state => state.faceSearch.searching,
    faceSearchError: state => state.faceSearch.error,
    faceSearchResults: state => state.faceSearch.results,
    detectedFaces: state => state.faceSearch.detectedFaces,
    uploadedImagePreview: state => state.faceSearch.uploadedImage,
    selectedFaceCluster: state => state.faceSearch.selectedCluster,
    hasFaceSearchResults: state => state.faceSearch.results.length > 0,
    hasDetectedFaces: state => state.faceSearch.detectedFaces.length > 0,
}


export const mutations = {
    pica_service_id(state, pica_service_id) {
        state.pica_service_id = pica_service_id
    },
    gallery_id(state, gallery_id) {
        state.gallery_id = gallery_id > 0 ? gallery_id : null
        state.media.sorting = null
    },
    configLoading(state, loading) {
        state.configLoading = loading
        if (loading) state.tabLoading = true
    },
    timeZone(state, timezone) {
        state.timeZone = timezone
    },
    filters(state, filters) {
        if (!filters) {
            filters = {date_range: {}}
        } else {
            filters = Object.fromEntries(
                Object.entries(filters).filter(([key, value]) => value !== null)
            )
        }
        state.filter.value = filters
    },
    addMedia(state, photos) {
        state.tabLoading = false
        if (photos.length === 0) return 0

        photos.map(photo => {
            if (state.media.storage[photo.id]) return null
            photo.shot_at = moment.tz(photo.shot_at, state.timeZone)
            Vue.set(state.media.storage, photo.id, photo)
        })
        updateMediaState(state)
    },
    newMediaUploaded(state, photos) {
        if (photos.length === 0) return

        let lastCreatedAt = state.media.lastCreatedAt

        const cluster = new MediaCluster(state.media.clusterBy, state.media.sorting)


        photos = photos.map(photo => {
            // Check if already present in list
            if (state.media.storage[photo.id]) return null

            // We need to add another layer of check, if we skip the media ( out of viewport or similar )
            // this could already be counted, but not added to the media storage
            // if we don't acknowledge this, we will count it again in timeline or in other position
            if (state.media.received[photo.id]) return null
            state.media.received[photo.id] = true

            // Normalize field with moment for consistency with media.storage
            photo.shot_at = moment.tz(photo.shot_at, state.timeZone)
            photo.created_at = moment.tz(photo.created_at, state.timeZone)

            // Update the lastCreatedAt data to avoid asking again for the same media
            if (!lastCreatedAt || photo.created_at > lastCreatedAt)
                lastCreatedAt = photo.created_at

            // If the new media are out of the viewport, we won't add it to the current visibile media
            if (state.media.hasBefore && cluster.isMediaBefore(photo, state.media.first)) return photo
            if (state.media.hasAfter && cluster.isMediaAfter(photo, state.media.last)) return photo

            // Add the media to the storage
            Vue.set(state.media.storage, photo.id, photo)
            return photo
        }).filter(photo => photo !== null)


        if (photos.length === 0) return

        Vue.set(state.media, 'lastCreatedAt', lastCreatedAt)
        updateMediaState(state)

        if (state.media.hasTimeline) {
            const timeLineManager = new TimeLine(state.timeline.storage, state.media.sorting)
            state.timeline.storage = timeLineManager.incrementCounters(photos, state.media.clusterBy)
            state.timeline.clusters = timeLineManager.getCluster(state.media.clusterBy)
        }
    },
    newMediaProcessed(state, photos) {
        if (photos.length === 0) return

        let lastProcessedAt = state.media.lastProcessedAt

        photos.map(photo => {
            if (!lastProcessedAt || photo.processed_at > lastProcessedAt)
                lastProcessedAt = photo.processed_at

            let oldMedia = state.media.storage[photo.id]
            if (!oldMedia) return

            oldMedia.processed_at = photo.processed_at
            Vue.set(state.media.storage, oldMedia.id, oldMedia)
        })

        Vue.set(state.media, 'lastProcessedAt', lastProcessedAt)
    },
    resetMedia(state) {
        state.tabLoading = true
        state.media = {
            storage: {},
            received: {},
            clusters: [],
            sorted: [],
            first: null,
            last: null,
            hasBefore: true,
            hasAfter: true,
            clusterBy: state.media.clusterBy,
            sorting: state.media.sorting,
            hasTimeline: state.media.hasTimeline,
            hideTimeline: state.media.hideTimeline,
            lastCreatedAt: state.media.lastCreatedAt,
            lastProcessedAt: state.media.lastProcessedAt,
            defaultSorting: state.media.defaultSorting,
        }
    },
    timeline(state, timeline) {
        state.timeline.storage = timeline.map(item => {
            item.date = moment.tz(item.date, state.timeZone)
            item.key = item.date.format('YYYYMMDDHHmm')
            return item
        })

        const timeLineManager = new TimeLine(state.timeline.storage, state.media.sorting)

        if (state.media.hideTimeline) {
            state.media.clusterBy = MediaCluster.BY_DAY
            state.timeline.clusters = timeLineManager.getCluster(state.media.clusterBy)
        } else {
            const optimal = timeLineManager.getOptimalClusterization()
            state.timeline.clusters = optimal.clusters
            state.media.clusterBy = optimal.clusterBy
        }
    },
    resetTimeline(state) {
        state.timeline = {
            storage: {},
            clusters: []
        }
    },
    setClusterBy(state, clusterBy) {
        state.media.clusterBy = clusterBy
        if (state.media.hasTimeline) {
            const timeLineManager = new TimeLine(state.timeline.storage, state.media.sorting)
            state.timeline.clusters = timeLineManager.getCluster(clusterBy)
        } else {
            state.timeline.clusters = []
        }

        const clusterManager = new MediaCluster(clusterBy, state.media.sorting)
        state.media.clusters = clusterManager.getClusters(state.media.sorted, state.timeline.clusters)
    },
    hasBefore(state, hasBefore) {
        state.media.hasBefore = hasBefore
    },
    hasAfter(state, hasAfter) {
        state.media.hasAfter = hasAfter
    },
    config(state, config) {
        state.permissions = {
            ...state.permissions,
            canUpdateTags: 'can_update_tags' in config,
            canFilterByPhotographers: 'can_filter_by_photographers' in config,
            canFilterByTags: 'can_filter_by_tags' in config,
            canFilterByPicaCode: 'can_filter_by_pica_code' in config,
            canFilterByRaceNumber: 'can_filter_by_race_number' in config,
            canFilterByShotAt: 'can_filter_by_shot_at' in config,
            canFilterByDeliveryStatus: 'can_filter_by_delivery_status' in config,
            canDownload: 'can_download' in config,
            canDownloadSingle: 'can_download_single' in config,
            canSetGallery: 'can_set_gallery' in config,
            canCopy: 'can_copy' in config,
            canDelete: 'can_delete' in config,
            canSortMedia: 'can_sort_media' in config,
            canUpload: 'can_upload' in config,
            canSearchByFileName: 'can_search_by_file_name' in config,
            canSearchById: 'can_search_by_id' in config,
            canSoftDelete: 'can_soft_delete' in config,
            canRestore: 'can_restore' in config,
            canSeeId: 'can_see_id' in config,
            canEditMediaUploadedByOther: 'can_edit_media_uploaded_by_other' in config,
            canExportMediaPicaCode: 'can_export_media_pica_code' in config,
            canSeeDeliverable: 'can_see_deliverable' in config,
            canSeeDelivered: 'can_see_delivered' in config,
            canSeeCheckin: 'can_see_checkin' in config,
            canSeeFaces: 'can_see_faces' in config,
            canUpdateFaceTags: 'can_update_face_tags' in config,
            canDeleteFace: 'can_delete_face' in config,
            canGenerateCheckinFromFace: 'can_generate_checkin_from_face' in config,
            canSetPicaCode: 'can_set_pica_code' in config,
            canUnlinkPicaCode: 'can_unlink_pica_code' in config,
            canArchive: 'can_archive' in config,
            canSeePhotographer: 'can_see_photographer' in config,
        }

        let clusterBy = MediaCluster.BY_DAY
        if (!config.timeline || state.media.sorting === GallerySorting.custom)
            clusterBy = MediaCluster.NO_CLUSTER

        state.media = {
            ...state.media,
            sorting: config.sorting,
            defaultSorting: config.default_sorting,
            lastCreatedAt: config.last_created_at ? moment.tz(config.last_created_at, state.timeZone) : null,
            lastProcessedAt: config.last_processed_at ? moment.tz(config.last_processed_at, state.timeZone) : null,
            hasTimeline: config.timeline,
            hideTimeline: config.hide_timeline,
            clusterBy: clusterBy
        }
        state.configLoading = false
    },
    sorting(state, sorting) {
        localStorage.setItem('photoSearch-sort-' + state.pica_service_id + '-' + state.gallery_id, sorting)
        state.media.sorting = sorting
    },
    filtersValue(state, filters) {
        if (!filters) {
            state.filter.options = null
            return
        }
        state.filter.options = {
            photographers: 'photographers' in filters ? filters.photographers : [],
            tags: 'tags' in filters ? filters.tags : []
        }
    },
    setSearchQuery(state, query) {
        if (query === null)
            query = {}
        state.search.query = query
    },
    searchResult(state, media) {
        if (!media || media.length <= 0) {
            state.search.storage = []
            state.search.results = null
            state.search.result = null
            return
        }

        const mediaCluster = new MediaCluster(state.media.clusterBy, state.media.sorting)
        const results = media.map(item => {
            item.shot_at = moment.tz(item.shot_at, state.timeZone)
            return item
        })
        state.search.storage = mediaCluster.getSortedMedia(results)
        state.search.results = state.search.storage.reduce((acc, photo) => {
            acc[photo.id] = photo
            return acc
        }, {})
    },
    setActiveSearchResult(state, index) {
        const result = state.search.storage[index]
        if (!result) {
            state.search.result = null
            return
        }

        state.search.result = {
            id: result.id,
            shot_at: result.shot_at,
            actual: index + 1,
            index: index,
            total: state.search.storage.length,
            hasPrev: index > 0,
            hasNext: index < state.search.storage.length - 1
        }
    },
    moveTop(state, selected) {
        // If we don't have the first media, we can't move the media to the top
        // ( we don't have the first order, nor we can show it until we are at the beginning )
        if (state.media.hasBefore) {
            selected.map(photo => {
                Vue.delete(state.media.storage, photo.id)
            })
        } else {
            let firstOrder = state.media.sorted[0].order
            selected.map(photo => {
                photo.order = --firstOrder
                Vue.set(state.media.storage, photo.id, photo)
            })
        }
        updateMediaState(state)
    },
    moveBottom(state, selected) {
        // If we don't have the last media, we can't move the media to the bottom
        // ( we don't have the last order, nor we can show it until we are at the end )
        if (state.media.hasAfter) {
            selected.map(photo => {
                Vue.delete(state.media.storage, photo.id)
            })
        } else {
            let lastOrder = state.media.sorted[state.media.sorted.length - 1].order
            selected.map(photo => {
                photo.order = ++lastOrder
                Vue.set(state.media.storage, photo.id, photo)
            })
        }
        updateMediaState(state)
    },
    reorder(state, {selected, before, after}) {
        const media = state.media.sorted
        let ids = selected.map(photo => parseInt(photo.id))
        const destination = before ?? after

        let orders = media.filter(item => ids.includes(item.id) || item.id === destination).reduce((acc, item) => {
            if (acc.first === null || item.order < acc.first) acc.first = item.order
            if (acc.last === null || item.order > acc.last) acc.last = item.order
            if (item.id === destination) acc.dest = item.order
            return acc
        }, {first: null, last: null, dest: null})

        let toReorder = media.filter(item => item.order >= orders.first && item.order <= orders.last).reduce((acc, item) => {
            if (ids.includes(item.id)) acc.selected.push(item)
            else if (item.id === destination) acc.dest.push(item)
            else acc.other.push(item)
            return acc
        }, {selected: [], other: [], dest: []})

        const reorderFunct = (media) => {
            media.order = orders.first++
            Vue.set(state.media.storage, media.id, media)
        }

        if (orders.dest === orders.last) {
            if (before > 0) {
                toReorder.other.map(reorderFunct)
                toReorder.selected.map(reorderFunct)
                toReorder.dest.map(reorderFunct)
            } else {
                toReorder.other.map(reorderFunct)
                toReorder.dest.map(reorderFunct)
                toReorder.selected.map(reorderFunct)
            }
        } else {
            if (before > 0) {
                toReorder.selected.map(reorderFunct)
                toReorder.dest.map(reorderFunct)
                toReorder.other.map(reorderFunct)
            } else {
                toReorder.dest.map(reorderFunct)
                toReorder.selected.map(reorderFunct)
                toReorder.other.map(reorderFunct)
            }
        }

        updateMediaState(state)
    },
    replaceMedia(state, {selected, media}) {
        // Update all media returned by backend
        media.map(photo => {
            photo.shot_at = moment.tz(photo.shot_at, state.timeZone)
            Vue.set(state.media.storage, photo.id, photo)
        })

        // Remove selected media that are NOT in the response
        selected.filter(photo => !media.find(m => m.id === photo.id)).map(photo => {
            Vue.delete(state.media.storage, photo.id)
        })

        updateMediaState(state)
    },
    availableTags(state, tags) {
        state.availableTags = tags
    },
    SET_TAG_DEFINITIONS(state, definitions) {
        state.tagDefinitions = definitions
    },
    setClusterNamePattern(state, pattern) {
        state.clusterNamePattern = pattern || ''
    },
    galleries(state, galleries) {
        state.galleries = galleries
    },
    detail(state, detail) {
        state.detail = detail
    },
    selectMedia(state, ids) {
        if (state.selected.shiftPressed) {
            Object.keys(state.selected.hovering).map(id => {
                ids.push(id)
            })
        }
        ids.map(id => {
            if (state.selected.deselected[id]) Vue.delete(state.selected.deselected, id)
            if (state.media.storage[id] && state.media.storage[id].selected) return
            Vue.set(state.selected.ids, id, true)
            state.selected.lastSelected = id
        })
    },
    deSelectMedia(state, ids) {
        ids.map(id => {
            if (state.selected.deselected[id]) return
            if (state.selected.ids[id]) Vue.delete(state.selected.ids, id)
            if (state.media.storage[id] && state.media.storage[id].selected)
                Vue.set(state.selected.deselected, id, true)
        })
    },
    addSelectAllFilter(state) {
        let filters = {...state.filter.value}
        if (filters.date_range && Object.keys(filters.date_range).length === 0)
            delete filters.date_range

        if (Object.keys(filters).length === 0)
            filters.all = true;

        // Controlla se un filtro equivalente esiste già
        const filterKey = JSON.stringify(filters)
        const alreadyExists = state.selected.filters.some(
            existing => JSON.stringify(existing) === filterKey
        )

        if (!alreadyExists) {
            state.selected.filters.push(filters)
        }
    },
    setServerCount(state, count) {
        state.selected.serverCount = count
    },
    markAllMediaAsSelected(state) {
        state.media.storage = Object.keys(state.media.storage).reduce((acc, id) => {
            let photo = state.media.storage[id]
            photo.selected = true
            acc[id] = photo
            return acc
        }, {})
        updateMediaState(state)
    },
    deSelectAll(state) {
        state.selected.filters = []
        state.selected.deselected = {}
        state.selected.ids = {}
        state.selected.hovering = {}
        state.selected.lastSelected = null
        state.selected.lastOver = null
        state.selected.serverCount = null
        state.media.storage = Object.keys(state.media.storage).reduce((acc, id) => {
            let photo = state.media.storage[id]
            photo.selected = false
            acc[id] = photo
            return acc
        },{})
        updateMediaState(state)
    },
    shiftPressed(state) {
        state.selected.shiftPressed = true
        state.selected.lastOver = null
        state.selected.hovering = {}
    },
    shiftReleased(state) {
        state.selected.shiftPressed = false
        state.selected.lastOver = null
        state.selected.hovering = {}
    },
    setOverMedia(state, id) {
        if (!state.selected.lastSelected || !state.selected.shiftPressed) {
            state.selected.hovering = {}
            return
        }
        state.selected.lastOver = id
        let lastSelectedIndex = state.media.sorted.findIndex(photo => photo.id === state.selected.lastSelected)
        let lastHoveredIndex = state.media.sorted.findIndex(photo => photo.id === state.selected.lastOver)
        if (lastSelectedIndex < 0 || lastHoveredIndex < 0) {
            state.selected.hovering = {}
            return
        }
        let start = Math.min(lastSelectedIndex, lastHoveredIndex)
        let end = Math.max(lastSelectedIndex, lastHoveredIndex)

        let hovering = {}
        for (let i = start; i <= end; i++)
            hovering[state.media.sorted[i].id] = true
        state.selected.hovering = hovering
    },
    // Face Search mutations
    setFaceSearching(state, searching) {
        state.faceSearch.searching = searching
    },
    setFaceSearchError(state, error) {
        state.faceSearch.error = error
    },
    setUploadedImage(state, imageUrl) {
        state.faceSearch.uploadedImage = imageUrl
    },
    setUploadedFile(state, file) {
        state.faceSearch.uploadedFile = file
    },
    setDetectedFaces(state, faces) {
        state.faceSearch.detectedFaces = faces
    },
    setFaceSearchResults(state, results) {
        state.faceSearch.results = results
    },
    setSelectedFaceCluster(state, cluster) {
        state.faceSearch.selectedCluster = cluster
    },
    clearFaceSearch(state) {
        if (state.faceSearch.uploadedImage) {
            URL.revokeObjectURL(state.faceSearch.uploadedImage)
        }
        state.faceSearch.searching = false
        state.faceSearch.error = null
        state.faceSearch.uploadedImage = null
        state.faceSearch.uploadedFile = null
        state.faceSearch.detectedFaces = []
        state.faceSearch.results = []
    }
}

// Debounced function to update selected stats
let updateSelectedStatsDebounced = null;

export const actions = service => ({
    setPicaServiceId({commit}, {picaServiceId, galleryId}) {
        service.abort()
        commit('pica_service_id', picaServiceId)
        commit('gallery_id', galleryId)
        commit('resetMedia')
        commit('resetTimeline')
        commit('setSearchQuery', null)
        commit('searchResult', null)
        commit('filters', null)
        commit('filtersValue', null)
        commit('availableTags', null)
        commit('galleries', null)
    }, setTimezone({commit}, timezone) {
        commit('timeZone', timezone)
    }, setFilters({commit}, filters) {
        service.abort()
        commit('resetMedia')
        commit('resetTimeline')
        commit('filters', filters)
    }, setSorting({commit}, sorting) {
        service.abort()
        commit('resetMedia')
        commit('resetTimeline')
        commit('sorting', sorting)
    }, setClusterBy({commit}, clusterBy) {
        commit('setClusterBy', clusterBy)
    }, async fetchBefore({commit, getters}) {
        const firstMedia = getters['firstMedia']
        if (!firstMedia) return
        if (!getters['hasBefore']) return

        const media = await service.fetchPhotos(getters.route_param, {
            ...getters.filters,
            ...getCursor(getters, firstMedia),
            cursor_before_id: firstMedia.id,
        }, getters.selectedFilters, getters.sorting)
        commit('addMedia', media)
        if (media.length < maxMediaInFilter) commit('hasBefore', false)
    }, async fetchAfter({commit, getters}) {
        const lastMedia = getters['lastMedia']
        if (!lastMedia) return
        if (!getters['hasAfter']) return
        const media = await service.fetchPhotos(getters.route_param, {
            ...getters.filters,
            ...getCursor(getters, lastMedia),
            cursor_after_id: lastMedia.id,
        }, getters.selectedFilters, getters.sorting)
        commit('addMedia', media)
        if (media.length < maxMediaInFilter) commit('hasAfter', false)
    }, async fetchFirstMedia({commit, getters}) {
        commit('resetMedia')
        const media = await service.fetchPhotos(getters.route_param, getters.filters, getters.selectedFilters, getters.sorting)
        commit('addMedia', media)
        if (media.length < maxMediaInFilter) commit('hasBefore', false)
        if (media.length < maxMediaInFilter) commit('hasAfter', false)
        commit('deSelectAll')
    }, async fetchForKey({commit, getters}, key) {
        const mediaCluster = new MediaCluster(getters.clusterBy)
        const range = mediaCluster.getDateRangeForKey(key, getters.timeZone)
        commit('resetMedia')
        const media = await service.fetchPhotos(getters.route_param, {
            ...getters.filters,
            date: getters.sorting === GallerySorting.shot_at_desc ? range.to : range.from,
        }, getters.selectedFilters, getters.sorting)
        commit('deSelectAll')
        commit('addMedia', media)
    }, async fetchFromId({commit, getters}, id) {
        commit('resetMedia')
        const media = await service.fetchPhotos(getters.route_param, {
            ...getters.filters,
            cursor_after_id: id,
            include_cursor: true
        }, getters.selectedFilters, getters.sorting)
        commit('addMedia', media)
    }, async initFromPhotoId({commit, dispatch}, {pica_service_id, gallery_id, photo_id}) {
        commit('pica_service_id', pica_service_id)
        commit('gallery_id', gallery_id)

        // Fetch config and photos starting from this photo_id
        await dispatch('fetchConfig')
        await dispatch('fetchFromId', photo_id)
    }, async downloadPhotos({getters, dispatch}, {ids, appyLogo, single_file} = {}) {
        const selected = createActionFilters(getters.selectedFilters, ids)
        const count = ids ? ids.length : getters.numSelected
        // Capture route_param to avoid issues if user navigates away during async download
        const routeParam = getters.route_param

        return await dispatch('downloader/download', {
            name: 'Photo Search',
            count,
            // Email download - backend sends link via email
            onEmail: async (email) => {
                await service.downloadPhotos(
                    routeParam,
                    selected,
                    email,
                    appyLogo,
                    single_file
                )
            },
            // Long task download - async via MQTT + polling
            onLongTask: () => service.downloadPhotosLongTask(routeParam, selected, appyLogo)
        }, { root: true })
    }, async fetchConfig({commit, getters}) {
        commit('configLoading', true)
        const config = await service.fetchConfig(getters.route_param, getters.sorting)
        commit('config', config)
        // Load cluster name pattern from archive_settings if available
        if (config?.archive_settings?.cluster_name_pattern) {
            commit('setClusterNamePattern', config.archive_settings.cluster_name_pattern)
        }
        commit('configLoading', false)
    }, async fetchTimeline({commit, getters}) {
        if (!getters['hasTimeline']) return
        const timeline = await service.fetchTimeline(getters.route_param, getters.filters, getters.sorting)
        commit('timeline', timeline)
    }, async fetchFilterOptions({commit, getters}) {
        const filters = await service.fetchFilterValues(getters.route_param)
        commit('filtersValue', filters)
    }, async fetchTags({commit, getters}) {
        if (getters['availableTags']) return
        commit('availableTags', await service.fetchTags(getters.route_param))
    }, async fetchTagDefinitions({commit, state}) {
        if (state.tagDefinitions.length > 0) return
        const definitions = await service.fetchTagDefinitions({pica_service_id: state.pica_service_id})
        commit('SET_TAG_DEFINITIONS', definitions)
    }, async fetchGalleries({commit, getters}) {
        if (getters['galleries']) return
        commit('galleries', await service.fetchGalleries(getters.route_param))
    }, async fetchIdsCluster({getters}, key) {
        // TODO: Rivedere, probabilmente la funzionalità andrebbe sostituita con un aggiunta alle query selected
        const mediaCluster = new MediaCluster(getters.clusterBy)
        const range = mediaCluster.getDateRangeForKey(key, getters.timeZone)
        return await service.fetchIdsForDate(getters.route_param, range.from, range.to, getters.filters, getters.sorting)
    }, async setSearchQuery({commit, getters}, query) {
        commit('setSearchQuery', query)
        commit('searchResult', null)
        if (!query) return

        const results = await service.fetchSearch(getters.route_param, query)
        commit('searchResult', results)
        commit('setActiveSearchResult', 0)
    }, prevSearchResult({commit, getters}) {
        if (!getters['searchResults']) return
        const index = getters['searchResult'].index
        commit('setActiveSearchResult', index - 1)
    }, nextSearchResult({commit, getters}) {
        if (!getters['searchResults']) return
        const index = getters['searchResult'].index
        commit('setActiveSearchResult', index + 1)
    }, async handleUpdateResponse({commit, getters, dispatch}, response) {
        // Centralized handler for all update operations
        if (response.refresh_needed) {
            // Case > 200: full refresh needed
            await dispatch('fetchFirstMedia')
        } else if (Array.isArray(response)) {
            // Case ≤ 200: response is directly the media array
            commit('replaceMedia', {selected: getters.selectedMedia, media: response})
        }
    }, async addTags({getters, dispatch}, tags) {
        const response = await service.addTags(
            getters.route_param,
            getters.selectedFilters,
            tags,
            getters.filters
        )
        await dispatch('handleUpdateResponse', response)
    }, async removeTags({getters, dispatch}) {
        const response = await service.removeTags(
            getters.route_param,
            getters.selectedFilters,
            getters.filters
        )
        await dispatch('handleUpdateResponse', response)
    }, async setMediaTags({getters, dispatch}, tags) {
        const response = await service.setMediaTags(
            getters.route_param,
            getters.selectedFilters,
            tags,
            getters.filters
        )
        await dispatch('handleUpdateResponse', response)
    }, async resetMediaTags({getters, dispatch}, tag_codes) {
        const response = await service.resetMediaTags(
            getters.route_param,
            getters.selectedFilters,
            tag_codes,
            getters.filters
        )
        await dispatch('handleUpdateResponse', response)
    }, async updateMediaTags({getters, dispatch}, operations) {
        const response = await service.updateMediaTags(
            getters.route_param,
            getters.selectedFilters,
            operations,
            getters.filters
        )
        await dispatch('handleUpdateResponse', response)
    }, async setGallery({getters, dispatch}, {gallery}) {
        const response = await service.setGallery(
            getters.route_param,
            getters.selectedFilters,
            gallery,
            getters.filters
        )
        await dispatch('handleUpdateResponse', response)
    }, async copyToGallery({getters}, {gallery}) {
        await service.copyToGallery(getters.route_param, getters.selectedFilters, gallery)
    }, async deleteMedia({getters, dispatch}) {
        const response = await service.deleteMedia(
            getters.route_param,
            getters.selectedFilters,
            getters.filters
        )
        await dispatch('handleUpdateResponse', response)
    }, async moveTop({commit, getters}) {
        await service.moveTop(getters.route_param, getters.selectedFilters)
        commit('moveTop', getters.selectedMedia)
    }, async moveBottom({commit, getters}) {
        await service.moveBottom(getters.route_param, getters.selectedFilters)
        commit('moveBottom', getters.selectedMedia)
    }, async newMediaUploaded({commit, getters}, taskId) {
        const newMedia = await service.fetchNewMedia(getters.route_param, getters.lastCreatedAt, taskId, getters.filters, getters.selectedFilters)
        if (newMedia.length <= 0) return
        commit('newMediaUploaded', newMedia)
    }, async newMediaProcessed({commit, getters}, taskId) {
        if (getters.idsInProcessing.length === 0) return
        const processedMedia = await service.fetchNewProcessed(getters.route_param, {
            processed_at: getters['lastProcessedAt'],
            ids: getters.idsInProcessing,
            taskId: taskId
        })
        commit('newMediaProcessed', processedMedia)
    }, async reorder({commit, getters}, {before, after}) {
        // for immediate feedback during drag and drop we commit the mutation before the request
        commit('reorder', {selected: getters.selectedMedia, before, after})
        await service.reorder(getters.route_param, getters.selectedFilters, before, after)
    }, async softDelete({getters, dispatch}) {
        const response = await service.softDelete(
            getters.route_param,
            getters.selectedFilters,
            getters.filters
        )
        await dispatch('handleUpdateResponse', response)
    }, async restore({getters, dispatch}) {
        const response = await service.restore(
            getters.route_param,
            getters.selectedFilters,
            getters.filters
        )
        await dispatch('handleUpdateResponse', response)
    }, async exportMediaPicaCode({getters}) {
        await service.exportMediaPicaCode(getters.route_param)
    }, async fetchMediaDetail({getters, commit}, id) {
        if (getters.detail && getters.detail.id === id) return
        commit('detail', null)
        commit('detail', await service.fetchMediaDetail(getters.route_param, id))
    }, selectMedia({commit, dispatch, state}, ids) {
        commit('selectMedia', ids)

        // If we're in server count mode, update the count with debounce
        if (state.selected.serverCount !== null) {
            if (!updateSelectedStatsDebounced) {
                updateSelectedStatsDebounced = debounce(() => {
                    dispatch('updateSelectedStats')
                }, 500)
            }
            updateSelectedStatsDebounced()
        }
    }, async updateSelectedStats({state, commit, getters}) {
        // Only update if we're in server count mode
        if (state.selected.serverCount === null) return

        try {
            const stats = await service.fetchSelectedStats(
                {
                    pica_service_id: state.pica_service_id,
                    gallery_id: state.gallery_id
                },
                getters.selectedFilters
            )

            if (stats && stats.num !== undefined) {
                commit('setServerCount', stats.num)
            }
        } catch (error) {
            // Silent fail - keep existing count
        }
    }, deSelectMedia({commit, dispatch, state}, ids) {
        commit('deSelectMedia', ids)

        // If we're in server count mode, update the count with debounce
        if (state.selected.serverCount !== null) {
            if (!updateSelectedStatsDebounced) {
                updateSelectedStatsDebounced = debounce(() => {
                    dispatch('updateSelectedStats')
                }, 500)
            }
            updateSelectedStatsDebounced()
        }
    }, async selectAll({commit, state, getters}) {
        commit('addSelectAllFilter')
        commit('markAllMediaAsSelected')

        // Fetch the actual count from backend
        try {
            const stats = await service.fetchSelectedStats(
                {
                    pica_service_id: state.pica_service_id,
                    gallery_id: state.gallery_id
                },
                getters.selectedFilters
            )

            if (stats && stats.num !== undefined) {
                commit('setServerCount', stats.num)
            }
        } catch (error) {
            // Fallback: use local count
            const localCount = Object.values(state.media.storage).length
            commit('setServerCount', localCount)
        }
    }, deSelectAll({commit}) {
        commit('deSelectAll')
    }, shiftPressed({commit}) {
        commit('shiftPressed')
    }, shiftReleased({commit}) {
        commit('shiftReleased')
    }, setOverMedia({commit}, id) {
        commit('setOverMedia', id)
    }, async generateCheckinFromFace({state, getters}) {
        await service.generateCheckinFromFace({
            pica_service_id: state.pica_service_id,
            gallery_id: state.gallery_id
        }, getters.selectedFilters)
    }, async setPicaCode({state, getters, dispatch}, {pica_code}) {
        const response = await service.setPicaCode({
            pica_service_id: state.pica_service_id,
            gallery_id: state.gallery_id
        }, getters.selectedFilters, pica_code, getters.filters)
        await dispatch('handleUpdateResponse', response)
    }, async unlinkPicaCode({state, getters, dispatch}, {pica_code}) {
        const response = await service.unlinkPicaCode({
            pica_service_id: state.pica_service_id,
            gallery_id: state.gallery_id
        }, getters.selectedFilters, pica_code, getters.filters)
        await dispatch('handleUpdateResponse', response)
    }, async validatePicaCode({state}, pica_code) {
        return await service.validatePicaCode({
            pica_service_id: state.pica_service_id,
            gallery_id: state.gallery_id
        }, pica_code)
    },

    // Person Identification Actions
    async identifyPersonInImage({commit, state, dispatch}, imageFile) {
        commit('setFaceSearching', true)
        commit('setFaceSearchError', null)
        commit('setDetectedFaces', [])
        commit('setFaceSearchResults', [])

        // Create preview URL
        const previewUrl = URL.createObjectURL(imageFile)
        commit('setUploadedImage', previewUrl)
        commit('setUploadedFile', imageFile)

        try {
            const response = await photoArchiveService.identifyPerson(
                { pica_service_id: state.pica_service_id },
                imageFile
            )

            // Success - store results
            commit('setFaceSearchResults', response.clusters || [])
            commit('setFaceSearching', false)

            // If only 1 result, auto-apply filter
            if (response.clusters && response.clusters.length === 1) {
                await dispatch('applyPersonFilter', response.clusters[0])
                return { autoApplied: true, cluster: response.clusters[0] }
            }

            return { autoApplied: false, clusters: response.clusters || [] }

        } catch (error) {
            commit('setFaceSearching', false)

            if (error instanceof MultipleFacesError) {
                // Multiple faces detected - show selection UI
                commit('setDetectedFaces', error.getFaces())
                return { multipleFaces: true, faces: error.getFaces() }
            } else {
                // Other error
                const errorMessage = error.response?.data?.detail || error.message || 'An error occurred'
                commit('setFaceSearchError', errorMessage)
                throw error
            }
        }
    },

    async identifyPersonFromSelection({commit, state, dispatch}, {faceIndex}) {
        // User selected a specific face from multiple detected
        const face = state.faceSearch.detectedFaces[faceIndex]
        if (!face || !state.faceSearch.uploadedFile) {
            throw new Error('No face or uploaded file available')
        }

        commit('setFaceSearching', true)
        commit('setFaceSearchError', null)
        commit('setDetectedFaces', [])
        commit('setFaceSearchResults', [])

        try {
            // Use identify-person-by-bbox endpoint with the selected face bbox
            const response = await photoArchiveService.identifyPersonByBbox(
                { pica_service_id: state.pica_service_id },
                state.faceSearch.uploadedFile,
                face.face_bbox
            )

            // Success - store results
            commit('setFaceSearchResults', response.clusters || [])
            commit('setFaceSearching', false)

            // Auto-apply filter if single person identified
            if (response.clusters && response.clusters.length === 1) {
                await dispatch('applyPersonFilter', response.clusters[0])
                return { autoApplied: true, cluster: response.clusters[0] }
            }

            return { autoApplied: false, clusters: response.clusters || [] }

        } catch (error) {
            commit('setFaceSearching', false)
            const errorMessage = error.response?.data?.detail || error.message || 'An error occurred'
            commit('setFaceSearchError', errorMessage)
            throw error
        }
    },

    async applyPersonFilter({commit, dispatch, state}, cluster) {
        // Store selected cluster for badge display
        commit('setSelectedFaceCluster', cluster)

        // Apply filter with cluster_id to find all photos of this person
        const newFilters = {
            ...state.filter.value,
            cluster_id: cluster.id
        }

        // Apply filters (this will trigger media reload)
        await dispatch('setFilters', newFilters)
    },

    clearPersonIdentification({commit}) {
        commit('clearFaceSearch')
    }
})

export default service => ({
    namespaced: true,
    state,
    getters,
    mutations,
    actions: actions(service)
})

